"use client";

import { useEffect } from "react";
import Image from "@perk/core/components/image";
import {
  TextBodySmall,
  TextHeadlineMed,
} from "@perk/core/components/typography";
import { ButtonLink } from "@perk/core/components/button-link";
import { appsignal } from "@perk/core/lib/appsignal";
import { cn } from "@perk/core/lib/utils";
import { usePathname } from "@perk/core/hooks/use-pathname";

interface HandleHomeClickEvent
  extends React.MouseEvent<HTMLAnchorElement, MouseEvent> {}

export function ErrorPage({
  error,
  server,
  reset = () => {},
  useMMs = true,
}: {
  error: Error & { digest?: string };
  server?: boolean;
  reset?: () => void;
  useMMs?: boolean;
}) {
  const pathname = usePathname();

  console.log({ error });

  useEffect(() => {
    appsignal.sendError(error);
  }, [error]);

  const handleHomeClick = (_e: HandleHomeClickEvent) => {
    if (pathname === "/") {
      reset();
      window.location.reload();
      return;
    }
  };

  return useMMs ? (
    <MmsErrorPage server={server} handleHomeClick={handleHomeClick} />
  ) : (
    <DefaultErrorPage server={server} handleHomeClick={handleHomeClick} />
  );
}

function MmsErrorPage({
  server,
  handleHomeClick,
}: {
  server?: boolean;
  handleHomeClick: (e: HandleHomeClickEvent) => void;
}) {
  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 py-12 w-full">
      <div className="max-w-md mx-auto text-center">
        <Image
          src="/not-found-page-mms.png"
          alt="Confused M&M'S character"
          width={200}
          height={200}
          unoptimized
          priority
          className="mx-auto mb-8"
        />

        <TextHeadlineMed className="mb-4 text-[#592512]">
          OOPS...
        </TextHeadlineMed>

        <TextBodySmall className="text-[#592512] mb-8">
          something went wrong. we&apos;re sorry for the inconvenience.
        </TextBodySmall>

        <ButtonLink
          href="/"
          className={cn("mb-5 border-[#592512] text-[#592512]", {
            "!rounded-[1rem]": server,
          })}
          variant="outline"
          onClick={handleHomeClick}
        >
          back to home
        </ButtonLink>

        <TextBodySmall className="text-gray-600">
          If this problem continues, send our team an email at
          <span className="text-[#964B00] underline"> hi@mmsfunclub.com</span>.
        </TextBodySmall>
      </div>
    </main>
  );
}

function DefaultErrorPage({
  server,
  handleHomeClick,
}: {
  server?: boolean;
  handleHomeClick: (e: HandleHomeClickEvent) => void;
}) {
  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 py-12 w-full">
      <div className="max-w-lg mx-auto text-center">
        <div className="bg-red-200 p-2 rounded-full inline-flex items-center justify-center">
          {/* biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="size-20 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-label="Error icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v4m0 4h.01M21.73 18l-8-14a2 2 0 00-3.48 0l-8 14A2 2 0 004 21h16a2 2 0 001.73-3z"
            />
          </svg>
        </div>

        <h1 className="text-3xl font-bold mt-8 mb-4">
          Oops! Something went wrong.
        </h1>
        <div className="text-gray-600 space-y-4">
          <p>
            We're sorry, but the page you were trying to access is currently
            unavailable.
          </p>
          <p>
            We've already been notified and are working on resolving the issue.
          </p>
          <p>
            Please try again later. If this issue persists, contact our team for
            assistance.
          </p>
        </div>

        <ButtonLink
          href="/"
          className={cn("my-5", {
            "!rounded-[2rem]": server,
          })}
          variant="outline"
          onClick={handleHomeClick}
        >
          back to home
        </ButtonLink>

        <p className="mt-4">
          Need help? Contact Us at{" "}
          <a href="mailto:support@perk.studio" className="text-perk-primary">
            support@perk.studio
          </a>
        </p>
      </div>
    </main>
  );
}
